/**
 * Roles jerárquicos: `viewer` < `analyst` < `admin`.
 *
 * Espejo exacto de `services/api/auth.py`. Si allá cambia el rango, acá
 * también — y hay un test que compara contra la lista del OpenAPI para que la
 * divergencia se note en CI y no en producción.
 *
 * **Esto no autoriza nada.** La autorización la hace la API. Acá se decide
 * qué mostrar: un botón que siempre devuelve 403 es peor que no tener el
 * botón. Ver `claims.ts`.
 */
import type { Role } from '@/api/types';

/** De menor a mayor. El orden ES el rango. */
export const ROLE_ORDER = ['viewer', 'analyst', 'admin'] as const satisfies readonly Role[];

export function rankOf(role: Role): number {
  const index = ROLE_ORDER.indexOf(role);
  // Un rol desconocido (la API agregó uno nuevo y el dashboard es viejo) vale
  // menos que el mínimo: se ve menos de lo que corresponde, no más.
  return index === -1 ? -1 : index;
}

/** ¿`role` alcanza `minimum`? */
export function hasRole(role: Role | null | undefined, minimum: Role): boolean {
  if (role === null || role === undefined) return false;
  return rankOf(role) >= rankOf(minimum);
}

/** Nombre visible. En español, como toda la UI. */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  analyst: 'Analista',
  viewer: 'Lector',
};

/** Una línea de qué puede hacer cada rol. Se muestra en la pantalla de 403. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'Administra usuarios, productos, lotes, etiquetas e integraciones.',
  analyst: 'Opera la bandeja de alertas: triage, investigación y cierre.',
  viewer: 'Consulta el dashboard. No modifica nada.',
};
